import React from "react";
import { useTranslation } from "react-i18next";

/**
 * Customer Statistics Cards Component
 * @param {Object} props
 * @param {Object} props.stats - Statistics data
 * @param {number} props.stats.totalActiveSubscriptions - Active subscriptions count
 * @param {number} props.stats.totalCompletedSubscriptions - Completed subscriptions count
 * @param {boolean} props.isVisible - Whether to show the stats cards
 */
const CustomerStatsCards = ({ stats, isVisible }) => {
  const { t } = useTranslation();

  if (!isVisible || !stats) return null;

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
      {/* Total Customers Card */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow duration-200">
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <p className="text-sm font-medium text-gray-500 uppercase tracking-wide">
              {t("customers.totalCustomers")}
            </p>
            <p className="text-3xl font-bold text-blue-600 mt-2">
              {(stats.totalActiveSubscriptions || 0) + (stats.totalCompletedSubscriptions || 0)}
            </p>
            <p className="text-sm text-gray-600 mt-1">
              {t("customers.allCustomers")}
            </p>
          </div>
          <div className="flex-shrink-0">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <svg
                className="w-6 h-6 text-blue-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                />
              </svg>
            </div>
          </div>
        </div>
      </div>
      
      {/* Active Customers Card */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow duration-200">
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <p className="text-sm font-medium text-gray-500 uppercase tracking-wide">
              {t("customers.activeCustomers")}
            </p>
            <p className="text-3xl font-bold text-green-600 mt-2">
              {stats.totalActiveSubscriptions || 0}
            </p>
            <p className="text-sm text-gray-600 mt-1">
              {t("customers.currentlyActive")}
            </p>
          </div>
          <div className="flex-shrink-0">
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <svg
                className="w-6 h-6 text-green-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
          </div>
        </div>
      </div>
      
      {/* Completed Customers Card */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow duration-200">
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <p className="text-sm font-medium text-gray-500 uppercase tracking-wide">
              {t("customers.completedCustomers")}
            </p>
            <p className="text-3xl font-bold text-gray-600 mt-2">
              {stats.totalCompletedSubscriptions || 0}
            </p>
            <p className="text-sm text-gray-600 mt-1">
              {t("customers.subscriptionsEnded")}
            </p>
          </div>
          <div className="flex-shrink-0">
            <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
              <svg
                className="w-6 h-6 text-gray-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
                />
              </svg>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerStatsCards;
